/**
 * acequia.browserdav — a WebDAV server that runs in a tab, headlessly.
 *
 *   const dav = await acequia.browserdav.start({
 *       group: 'frijoles-test',
 *       serverName: 'fixture',
 *       shares: [{ source: 'opfs', path: 'frijoles-test' }], // no gesture
 *       auth: { require: true },   // the default; `false` must be written out
 *   })
 *   const token = await dav.createToken()
 *   // dav.url, dav.addShare(...), dav.removeShare(name), dav.stop()
 *
 * OPFS shares are fully headless. FSA shares need a directory handle the
 * caller already holds; persisted FSA shares whose permission lapsed land in
 * dav.pendingShares (see onShareNeedsGesture) and re-grant via
 * dav.grantPendingShare(name) from a user gesture — the custom element's job.
 *
 * Closed by default: requests need a chain token minted by this device's own
 * user, except where a .acequia-access.json sidecar marks a directory
 * anonymously readable. See server.js for the full posture.
 *
 * A HEADLESS page — a test fixture, a data peer — has no user of its own, so
 * it cannot mint and would otherwise accept nothing. It declares whose tokens
 * open it instead; the owner mints in their own session and hands the token
 * to the consumer:
 *
 *   const dav = await acequia.browserdav.start({
 *       group: 'frijoles-test',
 *       serverName: 'fixture',
 *       shares: [{ source: 'opfs', path: 'frijoles-test' }],
 *       auth: { authUrl: 'https://relay.example/auth', owner: '<owner user id>' },
 *   })
 *   // dav.createToken() throws here, naming where to mint instead.
 *
 * With auth on and neither an identity nor auth.owner, start() refuses —
 * a server that can accept no token should not run quietly.
 */

import { BrowserDAVServer } from './server.js'

export { BrowserDAVServer }
// The protocol engine itself, for an app that serves a directory under its
// own routes and auth rules rather than as a BrowserDAV server — GeoCamera's
// read-only shot archive was the first (it imported the class from the
// BrowserDAV app's tree, which this module replaced; browserdav-as-an-
// endpoint-everywhere). Most apps want start() instead.
export { WebDAVFileSystem } from './webdav/WebDAVFileSystem.js'

/**
 * Create and start a BrowserDAV server.
 * @param {object} options - see BrowserDAVServer constructor
 * @returns {Promise<BrowserDAVServer>}
 */
export async function start(options = {}) {
    const server = new BrowserDAVServer(options)
    await server.start()
    return server
}
